import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { DateTime } from "luxon";

/**
 * Validates that a round's end time is after its start time.
 * Expects a FormGroup with 'roundTimeStart' and 'roundTimeEnd' controls containing ISO datetime strings.
 */
export function roundTimeValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const startTime: string | null = group.get("roundTimeStart")?.value;
    const endTime: string | null = group.get("roundTimeEnd")?.value;

    if (!startTime || !endTime) {
      return null; // Let required validator handle missing values
    }

    // Parse as UTC for consistent comparison
    const startDt = DateTime.fromISO(startTime, { zone: "utc" });
    const endDt = DateTime.fromISO(endTime, { zone: "utc" });

    if (!startDt.isValid || !endDt.isValid) {
      return null;
    }

    if (startDt >= endDt) {
      return { endTimeBeforeStart: true };
    }

    return null;
  };
}

/**
 * Validates that rounds in a FormArray don't have overlapping time intervals.
 * Expects each FormGroup to have 'roundTimeStart' and 'roundTimeEnd' controls containing ISO datetime strings.
 */
export function roundOverlapValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    const rounds = control.controls as FormGroup[];
    const intervals: { start: DateTime; end: DateTime }[] = [];

    for (const group of rounds) {
      const startTime: string | null = group.get("roundTimeStart")?.value;
      const endTime: string | null = group.get("roundTimeEnd")?.value;

      if (!startTime || !endTime) continue;

      const startDt = DateTime.fromISO(startTime, { zone: "utc" });
      const endDt = DateTime.fromISO(endTime, { zone: "utc" });

      if (startDt.isValid && endDt.isValid && startDt < endDt) {
        intervals.push({ start: startDt, end: endDt });
      }
    }

    // Check for overlaps
    const hasOverlap = intervals.some((interval, i) =>
      intervals.slice(i + 1).some((other) => {
        return interval.start < other.end && interval.end > other.start;
      })
    );

    return hasOverlap ? { overlappingRounds: true } : null;
  };
}
